import React, { FunctionComponent, PropsWithChildren } from 'react'
import { useCheckoutState, useDisplayProcessingModal } from '../../state'
import { CheckoutStatus } from '../../model'

type StatusCondition = (status: CheckoutStatus) => boolean

interface CheckoutStatusGuardProps extends PropsWithChildren {
  condition: StatusCondition
  /**
   * Tear down the guarded subtree once the checkout has confirmed AND the shared ProcessingModal is
   * covering the screen. On by default so state-reading components (e.g. OrderBreakdown) unmount
   * before `close.ts` `clearSession` purges the redux state, while the modal hides the transition.
   * Gating on the modal (not status alone) keeps the summary up through the post-confirm OAuth window
   * — avoiding a blank frame and avoiding racing the merchant redirect (the modal's auto-close only
   * runs after OAuth has written the callback params). Set to false for paths that own their own
   * close flow and never show the shared modal — notably Apple Pay Later, whose redirect uses
   * `useCloseApplePaylater`/`merchantRedirectUrl`.
   */
  tearDownOnConfirm?: boolean
}

export const CheckoutStatusGuard: FunctionComponent<CheckoutStatusGuardProps> = ({ condition, children, tearDownOnConfirm = true }) => {
  const { status } = useCheckoutState()
  const displayProcessingModal = useDisplayProcessingModal()

  // Tear down only once the checkout has confirmed AND the ProcessingModal (rendered at the layout
  // level) is covering the screen, so the guarded subtree unmounts before close.ts clearSession
  // purges state — without a blank frame during the post-confirm OAuth window.
  if (tearDownOnConfirm && status >= CheckoutStatus.Confirmed && displayProcessingModal) {
    return null
  }

  if (!condition(status)) {
    return null
  }

  return <>{children}</>
}

interface PreconfiguredGuardProps extends PropsWithChildren {
  tearDownOnConfirm?: boolean
}

// Pre-configured guards for common use cases
export const NoErrorStatusGuard: FunctionComponent<PreconfiguredGuardProps> = ({ children, tearDownOnConfirm }) => (
  <CheckoutStatusGuard condition={(status) => status !== CheckoutStatus.Error} tearDownOnConfirm={tearDownOnConfirm}>
    {children}
  </CheckoutStatusGuard>
)

export const StartedStatusGuard: FunctionComponent<PreconfiguredGuardProps> = ({ children, tearDownOnConfirm }) => (
  <CheckoutStatusGuard condition={(status) => status >= CheckoutStatus.Started} tearDownOnConfirm={tearDownOnConfirm}>
    {children}
  </CheckoutStatusGuard>
)
